import { useLingui } from '@lingui/react/macro';

import { useFieldsSchema } from '@/components/form/useFieldsSchema';
import { countries } from '@/constants/countries';

// Updated props to only take what's necessary for schema generation defaults
interface UseBillingFormSchemaProps {
  initialIsCompany?: boolean;
}

export function useBillingFormSchema(props?: UseBillingFormSchemaProps) {
  const { t } = useLingui();
  const initialIsCompany = props?.initialIsCompany ?? false;

  const { schema: billingFormSchema } = useFieldsSchema(
    [
      {
        name: 'isCompany',
        label: t`Bill to a company?`,
        type: 'checkbox',
        uiControl: 'switch',
        defaultValue: initialIsCompany,
      },
      {
        $relevant: {
          $when: ({ formState }) => !!formState?.values?.isCompany,
          $fields: [
            {
              name: 'companyName',
              label: t`Company Name`,
              type: 'text',
            },
            {
              name: 'registrationNumber',
              label: t`Registration Number (IČO)`,
              type: 'text',
            },
            {
              name: 'vatId',
              label: t`VAT ID (DIČ)`,
              type: 'text',
            },
          ],
        },
      },
      {
        name: 'addressLine1',
        label: t`Address Line 1`,
        type: 'text',
        required: true,
      },
      {
        name: 'addressLine2',
        label: t`Address Line 2 (Optional)`,
        type: 'text',
      },
      { name: 'city', label: t`City`, type: 'text', required: true },
      {
        name: 'postalCode',
        label: t`Postal Code`,
        type: 'text',
        required: true,
      },

      // TODO: Add a dropdown with the countries
      {
        name: 'country',
        label: t`Country`,
        type: 'select',
        required: true,
        options: countries.reduce(
          (acc, { name, code }) => {
            acc[code] = name;

            return acc;
          },
          {} as Record<string, string>,
        ),
      },
      // TODO: Not used right now, but will be used in the future to redirect emails to desired email
      {
        name: 'billingEmail',
        type: 'text',
        label: t`Billing Email (Optional)`,
        description: t`If you want to receive invoices to a different email address, enter it here.`,
        tooltip: t`If you want to receive invoices to a different email address, enter it here.`,
        uiProps: { type: 'email' },
      },
      {
        name: 'invoiceCcEmail',
        type: 'text',
        label: t`Invoice CC Email (Optional)`,
        description: t`A copy of every invoice is also sent to this address (e.g. your accountant). For a partner-managed organization, this is prefilled from the assigned partner rep the first time — edit or clear it as needed. Left blank on a partner-managed organization, invoice copies fall back to the partner rep.`,
        tooltip: t`A copy of every invoice is also sent to this address (e.g. your accountant). For a partner-managed organization, this is prefilled from the assigned partner rep the first time — edit or clear it as needed. Left blank on a partner-managed organization, invoice copies fall back to the partner rep.`,
        uiProps: { type: 'email' },
      },
    ],
    [t, initialIsCompany], // Dependency only on initialIsCompany
  );

  return { billingFormSchema };
}
